import { useMemo, useState, type FormEvent } from 'react'
import { router, useForm } from '@inertiajs/react'
import { FlaskConical, Pause, Pencil, Plus, RotateCcw, Trash2, Webhook } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import type { WebhookCopy, WebhookData, WebhookEndpoint, WebhookGroup } from '@/frontend/pages/Dashboard/DigitalWorkers/types'

interface Props {
  data: WebhookData | null
  workerId: string
  canManage: boolean
  copy: WebhookCopy
}

type WebhookFormData = {
  agent_id: string
  endpoint_id: string
  public_path: string
  methods: string
  provider: string
  channel: string
  auth_type: string
  secret_ref: string
  auth_header: string
  hmac_algorithm: string
  hmac_prefix: string
  explicit_risk_accepted: boolean
  wake_mode: string
  deliver: boolean
  instruction: string
}

const DEFAULT_AGENT = { id: 'main', name: 'main' }

function endpointPath(workerId: string, endpoint: WebhookEndpoint): string {
  return `/dashboard/digital_workers/${workerId}/agents/${encodeURIComponent(endpoint.agentId)}/webhooks/${encodeURIComponent(endpoint.id)}`
}

function endpointKey(endpoint: WebhookEndpoint): string {
  return `${endpoint.agentId}:${endpoint.id}`
}

function groupEndpoints(data: WebhookData): WebhookGroup[] {
  if (data.groups.length > 0) return data.groups

  const groups = new Map<string, WebhookEndpoint[]>()
  data.endpoints.forEach((endpoint) => {
    const endpoints = groups.get(endpoint.agentId) ?? []
    endpoints.push(endpoint)
    groups.set(endpoint.agentId, endpoints)
  })

  return Array.from(groups.entries()).map(([agentId, endpoints]) => ({ agentId, endpoints }))
}

function agentName(data: WebhookData, agentId: string): string {
  return data.agents.find((agent) => agent.id === agentId)?.name ?? agentId
}

function statusBadge(endpoint: WebhookEndpoint, copy: WebhookCopy) {
  if (endpoint.active) return <Badge variant="success">{copy.status.active}</Badge>
  if (endpoint.status === 'removed') return <Badge variant="critical">{copy.status.removed}</Badge>
  return <Badge variant="warning">{copy.status.paused}</Badge>
}

function authTypeLabel(type: string | undefined, copy: WebhookCopy): string {
  return type ? (copy.form.auth_options[type] ?? type) : copy.details.unknown
}

function wakeModeLabel(wakeMode: string | undefined, copy: WebhookCopy): string {
  return wakeMode === 'next-heartbeat' ? copy.form.wake_options.next_heartbeat : copy.form.wake_options.now
}

export default function WebhooksTab({ data, workerId, canManage, copy }: Props) {
  const agents = data?.agents.length ? data.agents : [DEFAULT_AGENT]
  const defaultAgentId = agents[0]?.id ?? DEFAULT_AGENT.id
  const [rotationRefs, setRotationRefs] = useState<Record<string, string>>({})
  const form = useForm<WebhookFormData>({
    agent_id: defaultAgentId,
    endpoint_id: 'webhook-inbound',
    public_path: '/webhooks/inbound',
    methods: 'POST',
    provider: 'external',
    channel: 'inbound',
    auth_type: 'shared_secret',
    secret_ref: 'env:WEBHOOK_SECRET',
    auth_header: 'X-Webhook-Token',
    hmac_algorithm: 'SHA256',
    hmac_prefix: '',
    explicit_risk_accepted: false,
    wake_mode: 'now',
    deliver: false,
    instruction: copy.form.default_instruction,
  })

  const groups = useMemo(() => data && data.status === 'ok' ? groupEndpoints(data) : [], [data])
  const configuredAgentIds = useMemo(() => new Set(agents.map((agent) => agent.id)), [agents])
  const selectedAgentId = agents.some((agent) => agent.id === form.data.agent_id) ? form.data.agent_id : defaultAgentId
  const createDisabled = !canManage || !data || data.status !== 'ok' || form.processing || !form.data.endpoint_id.trim() || !form.data.public_path.trim() || !form.data.provider.trim() || !form.data.channel.trim() || !form.data.instruction.trim()

  const reloadWebhooks = () => router.reload({ only: ['webhooks'] })
  const createPath = `/dashboard/digital_workers/${workerId}/agents/${encodeURIComponent(selectedAgentId)}/webhooks`

  const handleCreate = (event: FormEvent) => {
    event.preventDefault()
    if (createDisabled) return

    form.post(createPath, {
      preserveScroll: true,
      onSuccess: () => reloadWebhooks(),
    })
  }

  const editEndpoint = (endpoint: WebhookEndpoint) => {
    form.setData({
      ...form.data,
      agent_id: endpoint.agentId,
      endpoint_id: endpoint.id,
      public_path: endpoint.publicPath,
      methods: endpoint.methods.join(','),
      provider: endpoint.provider,
      channel: endpoint.channel,
      auth_type: endpoint.auth?.type ?? 'shared_secret',
      secret_ref: endpoint.auth?.secretRef ?? '',
      auth_header: endpoint.auth?.header ?? '',
      hmac_algorithm: endpoint.auth?.algorithm ?? 'SHA256',
      hmac_prefix: endpoint.auth?.prefix ?? '',
      explicit_risk_accepted: endpoint.auth?.explicitRiskAccepted === true,
      wake_mode: endpoint.delivery?.wakeMode === 'next-heartbeat' ? 'next-heartbeat' : 'now',
      deliver: endpoint.delivery?.deliver === true,
      instruction: endpoint.delivery?.instruction ?? copy.form.default_instruction,
    })
  }

  const runAction = (endpoint: WebhookEndpoint, action: 'test' | 'pause' | 'rotate' | 'delete') => {
    const path = endpointPath(workerId, endpoint)
    const options = { preserveScroll: true, onSuccess: reloadWebhooks }

    if (action === 'test') router.post(`${path}/test`, {}, options)
    if (action === 'pause') router.patch(`${path}/pause`, {}, options)
    if (action === 'rotate') {
      const secretRef = (rotationRefs[endpointKey(endpoint)] ?? '').trim()
      if (!secretRef) return

      router.post(`${path}/rotate`, { secret_ref: secretRef, window_seconds: 900 }, options)
    }
    if (action === 'delete') router.delete(path, options)
  }

  if (!data) {
    // Estado vazio do DS: uma frase só, média — sem caixa.
    return <p className="py-6 text-[13px] text-muted-foreground">{copy.load_failed}</p>
  }

  if (data.status !== 'ok') {
    return (
      <Alert variant="warning">
        <Webhook className="h-4 w-4" />
        <AlertTitle>{copy.unavailable.title}</AlertTitle>
        <AlertDescription>{data.error || copy.unavailable.default_error}</AlertDescription>
      </Alert>
    )
  }

  return (
    <div className="space-y-4">
      <Card className="gap-4">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-[14px] font-semibold">
            <Webhook className="h-4 w-4 text-muted-foreground" />
            {copy.title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCreate} className="grid gap-4 lg:grid-cols-[minmax(9rem,0.9fr)_minmax(9rem,1fr)_minmax(10rem,1fr)_minmax(8rem,0.8fr)_auto] lg:items-end">
            <div className="grid gap-1.5">
              <Label htmlFor="webhook-agent">{copy.form.agent}</Label>
              <Select value={selectedAgentId} onValueChange={(value) => form.setData('agent_id', value)} disabled={!canManage}>
                <SelectTrigger id="webhook-agent"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {agents.map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>{agent.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="webhook-endpoint">{copy.form.endpoint}</Label>
              <Input
                id="webhook-endpoint"
                value={form.data.endpoint_id}
                onChange={(event) => form.setData('endpoint_id', event.target.value)}
                disabled={!canManage}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="webhook-path">{copy.form.public_path}</Label>
              <Input
                id="webhook-path"
                value={form.data.public_path}
                onChange={(event) => form.setData('public_path', event.target.value)}
                disabled={!canManage}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="webhook-secret">{copy.form.secret_ref}</Label>
              <Input
                id="webhook-secret"
                value={form.data.secret_ref}
                onChange={(event) => form.setData('secret_ref', event.target.value)}
                disabled={!canManage || form.data.auth_type === 'none'}
              />
            </div>
            <Button type="submit" disabled={createDisabled}>
              <Plus className="h-4 w-4" />
              {copy.form.create}
            </Button>
            <div className="grid gap-1.5">
              <Label htmlFor="webhook-auth">{copy.form.auth}</Label>
              <Select value={form.data.auth_type} onValueChange={(value) => form.setData('auth_type', value)} disabled={!canManage}>
                <SelectTrigger id="webhook-auth"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="shared_secret">{copy.form.auth_options.shared_secret}</SelectItem>
                  <SelectItem value="bearer">{copy.form.auth_options.bearer}</SelectItem>
                  <SelectItem value="hmac">{copy.form.auth_options.hmac}</SelectItem>
                  <SelectItem value="none">{copy.form.auth_options.none}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="webhook-methods">{copy.form.methods}</Label>
              <Input
                id="webhook-methods"
                value={form.data.methods}
                onChange={(event) => form.setData('methods', event.target.value)}
                disabled
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="webhook-provider">{copy.form.provider}</Label>
              <Input
                id="webhook-provider"
                value={form.data.provider}
                onChange={(event) => form.setData('provider', event.target.value)}
                disabled={!canManage}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="webhook-channel">{copy.form.channel}</Label>
              <Input
                id="webhook-channel"
                value={form.data.channel}
                onChange={(event) => form.setData('channel', event.target.value)}
                disabled={!canManage}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="webhook-auth-header">{copy.form.auth_header}</Label>
              <Input
                id="webhook-auth-header"
                value={form.data.auth_header}
                onChange={(event) => form.setData('auth_header', event.target.value)}
                disabled={!canManage || form.data.auth_type === 'none'}
              />
            </div>
            <label className="flex min-h-9 items-center gap-2 text-sm text-muted-foreground">
              <Checkbox
                checked={form.data.explicit_risk_accepted}
                onCheckedChange={(checked) => form.setData('explicit_risk_accepted', checked === true)}
                disabled={!canManage || form.data.auth_type !== 'none'}
              />
              {copy.form.no_auth}
            </label>
            <div className="grid gap-1.5">
              <Label htmlFor="webhook-wake-mode">{copy.form.wake_mode}</Label>
              <Select value={form.data.wake_mode} onValueChange={(value) => form.setData('wake_mode', value)} disabled={!canManage}>
                <SelectTrigger id="webhook-wake-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="now">{copy.form.wake_options.now}</SelectItem>
                  <SelectItem value="next-heartbeat">{copy.form.wake_options.next_heartbeat}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <label className="flex min-h-9 items-center gap-2 text-sm text-muted-foreground">
              <Checkbox
                checked={form.data.deliver}
                onCheckedChange={(checked) => form.setData('deliver', checked === true)}
                disabled={!canManage}
              />
              {copy.form.deliver}
            </label>
            <div className="grid gap-1.5 lg:col-span-5">
              <Label htmlFor="webhook-instruction">{copy.form.instruction}</Label>
              <Textarea
                id="webhook-instruction"
                value={form.data.instruction}
                onChange={(event) => form.setData('instruction', event.target.value)}
                disabled={!canManage}
                className="min-h-24 resize-y"
              />
            </div>
          </form>
        </CardContent>
      </Card>

      {groups.length === 0 ? (
        // Estado vazio do DS: uma frase só, média.
        <p className="py-4 text-[13px] text-muted-foreground">{copy.empty.body}</p>
      ) : (
        groups.map((group) => (
          <Card key={group.agentId} className="py-0">
            <CardHeader className="border-b py-4">
              <CardTitle className="text-[14px] font-semibold">
                {agentName(data, group.agentId)}
                <span className="ml-2 font-mono text-[11px] font-normal text-faint-foreground">{group.agentId}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="divide-y p-0">
              {group.endpoints.map((endpoint) => (
                <div key={`${endpoint.agentId}:${endpoint.id}`} className="grid gap-3 p-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
                  <div className="min-w-0 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-sm font-medium text-foreground">{endpoint.publicPath}</span>
                      {endpoint.methods.map((method) => <Badge key={method} variant="secondary">{method}</Badge>)}
                      {statusBadge(endpoint, copy)}
                    </div>
                    <div className="grid gap-1 text-xs text-muted-foreground sm:grid-cols-2">
                      <span className="truncate">{copy.details.url}: {endpoint.publicUrl || `${data.host || endpoint.host || copy.details.host_placeholder}${endpoint.publicPath}`}</span>
                      <span>{copy.details.auth}: {authTypeLabel(endpoint.auth?.type, copy)}{endpoint.auth?.secretRef ? ` (${endpoint.auth.secretRef})` : ''}</span>
                      <span>{copy.details.provider}: {endpoint.provider}/{endpoint.channel}</span>
                      <span>{copy.details.sink}: {endpoint.delivery?.type || copy.details.unknown} -&gt; {endpoint.delivery?.agentId || endpoint.agentId}</span>
                      <span>{copy.details.wake_mode}: {wakeModeLabel(endpoint.delivery?.wakeMode, copy)}</span>
                      <span>{copy.details.delivery}: {endpoint.delivery?.deliver ? copy.details.delivery_enabled : copy.details.delivery_disabled}</span>
                      <span className="break-words sm:col-span-2">{copy.details.instruction}: {endpoint.delivery?.instruction || copy.details.unknown}</span>
                      {endpoint.auth?.previousSecretAcceptsUntil && <span>{copy.details.previous_secret_until} {endpoint.auth.previousSecretAcceptsUntil}</span>}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 lg:justify-end">
                    <Input
                      aria-label={copy.actions.new_secret_ref}
                      className="h-9 w-56"
                      value={rotationRefs[endpointKey(endpoint)] ?? ''}
                      placeholder={copy.actions.new_secret_ref_placeholder}
                      onChange={(event) => setRotationRefs((current) => ({ ...current, [endpointKey(endpoint)]: event.target.value }))}
                      disabled={!canManage || endpoint.status === 'removed' || endpoint.auth?.type === 'none'}
                    />
                    <Button type="button" variant="outline" size="sm" disabled={!canManage || endpoint.status === 'removed' || !configuredAgentIds.has(endpoint.agentId) || endpoint.canEditExecution !== true} onClick={() => editEndpoint(endpoint)}>
                      <Pencil className="h-4 w-4" />
                      {copy.actions.edit}
                    </Button>
                    <Button type="button" variant="outline" size="sm" disabled={!canManage || !endpoint.active} onClick={() => runAction(endpoint, 'test')}>
                      <FlaskConical className="h-4 w-4" />
                      {copy.actions.test}
                    </Button>
                    <Button type="button" variant="outline" size="sm" disabled={!canManage || !endpoint.active} onClick={() => runAction(endpoint, 'pause')}>
                      <Pause className="h-4 w-4" />
                      {copy.actions.pause}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={!canManage || endpoint.status === 'removed' || endpoint.auth?.type === 'none' || !(rotationRefs[endpointKey(endpoint)] ?? '').trim()}
                      onClick={() => runAction(endpoint, 'rotate')}
                    >
                      <RotateCcw className="h-4 w-4" />
                      {copy.actions.rotate}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                      disabled={!canManage || endpoint.status === 'removed'}
                      onClick={() => runAction(endpoint, 'delete')}
                    >
                      <Trash2 className="h-4 w-4" />
                      {copy.actions.remove}
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        ))
      )}
    </div>
  )
}
